import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { BaseModal } from '../../components';
import {
  CheckOutlined,
  CloseOutlined,
  CloudQueueOutlined,
  FileCopyOutlined,
} from '@material-ui/icons';
import { useSpeakeasyTrades } from '../../modules/Trades/SpeakeasyTrades';
import { AppContext } from '../../modules/AppContext';
import { usePermissions } from '../../modules/Permissions';
import { LoaderSubmitButton } from '../../components/LoaderSubmitButton';
import { useHistory } from 'react-router';
import { formatEasternDateTime, formatEasternDateTimeFromUtc } from '../../utils/DateUtil';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    textAlign: 'left',
  },
  buttonBox: {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    marginTop: theme.spacing(2),
  },
  titleBox: {
    width: '25%',
    display: 'flex',
    alignItems: 'center',
  },
  valueBox: {
    width: '65%',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(0.5),
  },
  actionBox: {
    width: '10%',
    display: 'flex',
    alignItems: 'center',
  },
  row: {
    padding: theme.spacing(1),
    width: '100%',
    display: 'flex',
  },
  jobTable: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  codeButton: {
    padding: '0.75rem 1.25rem',
    height: '0.75rem',
  },
}));

export const SpeakeasyJobModal = ({ open, handleClose, ...props }) => {
  const classes = useStyles();
  const history = useHistory();
  const speakeasy = useSpeakeasyTrades();
  const { hasAndromedaAccess } = usePermissions();
  const { showToast } = useContext(AppContext);
  const rows = [
    {
      title: 'Job Id',
      key: 'id',
    },
    {
      title: 'Participant Id',
      key: 'participantId',
    },
    {
      title: 'Participant',
      key: 'participantName',
    },
    {
      title: 'Advisor',
      key: 'advisorName',
    },
    {
      title: 'Product',
      key: 'product',
    },
    {
      title: 'Status',
      key: 'status',
    },
    {
      title: 'Type',
      key: 'type',
    },
    {
      title: 'Code Sent',
      key: 'codeSent',
      format: formatEasternDateTime,
    },
    {
      title: 'Code Received',
      key: 'codeReceived',
      format: formatEasternDateTimeFromUtc,
        },
      ];

  const [showCode, setShowCode] = useState(false);
  const [converting, setConverting] = useState(false);

  const handleConvert = async () => {
    setConverting(true);
    const res = await speakeasy.convertToAndromedaJob(props.id);
    if (res.ok) {
      setConverting(false);
      showToast({
        message: 'Job successfully sent to Andromeda',
        type: 'success',
      });
    } else {
      setConverting(false);
      showToast({
        message: 'Error sending job to Andromeda',
        type: 'error',
      });
    }
  };

  useEffect(() => {
    return () => {
      setShowCode(false);
      setConverting(false);
    };
  }, []);

  return (
    <BaseModal
      isOpen={open}
      handleClose={handleClose}
      title="Speakeasy Job Details"
      content={
        <Box className={classes.wrapper}>
          <Box className={classes.jobTable}>
            {rows.map((row) => (
              <Box className={classes.row} key={row.key}>
                <Typography variant="body1" className={classes.titleBox}>
                  <strong>{row.title}:</strong>
                </Typography>
                <Typography variant="body1" className={classes.valueBox}>
                  {row.format ? row.format(props[row.key]) : props[row.key]}
                </Typography>

                <Box className={classes.actionBox}>
                  <IconButton
                    size="small"
                    color="primary"
                    title="Copy to clipboard"
                    onClick={() =>
                      navigator.clipboard.writeText(
                        row.format ? row.format(props[row.key]) : props[row.key]
                      )
                    }
                  >
                    <FileCopyOutlined />
                  </IconButton>
                </Box>
              </Box>
            ))}
            <Box className={classes.row}>
              <Typography variant="body1" className={classes.titleBox}>
                <strong>Save Phone: </strong>
              </Typography>
              <Box className={classes.valueBox}>
                {props?.saveFutureCapitalPhone ? (
                  <CheckOutlined htmlColor="green" />
                ) : (
                  <CloseOutlined htmlColor="red" />
                )}
              </Box>
              <Box className={classes.actionBox}></Box>
            </Box>
            {(props?.code ?? false) && (
              <Box className={classes.row}>
                <Typography variant="body1" className={classes.titleBox}>
                  <strong>Code :</strong>
                </Typography>
                <Box className={classes.valueBox}>
                  {showCode ? (
                    <Typography variant="body1">{props.code}</Typography>
                  ) : (
                    <Button
                      className={classes.codeButton}
                      variant="outlined"
                      onClick={() => setShowCode(true)}
                    >
                      View
                    </Button>
                  )}
                </Box>
                <Box className={classes.actionBox}></Box>
              </Box>
            )}
          </Box>
          {hasAndromedaAccess && props.andromedaCompatible && (
            <Box className={classes.buttonBox}>
              {!!props.andromedaJobId ? (
                <Box mt={2}>
                  <Button
                    variant="outlined"
                    onClick={() =>
                      history.push(
                        `/dashboard/andromeda/job?id=${props.andromedaJobId}`
                      )
                    }
                  >
                    View Andromeda Job
                  </Button>
                </Box>
              ) : (
                props?.status === 'readyForCode' && (
                  <Box mt={2}>
                    <LoaderSubmitButton
                      onClick={() => handleConvert()}
                      loading={converting}
                      icon={<CloudQueueOutlined />}
                    >
                      Send to Andromeda
                    </LoaderSubmitButton>
                  </Box>
                )
              )}
            </Box>
          )}
        </Box>
      }
    />
  );
};
